import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  schemaJsonLd?: Record<string, unknown>[];
}

export default function SEO({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogType = 'website',
  schemaJsonLd,
}: SEOProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, attrName = 'name') => {
      let el = document.querySelector(`meta[${attrName}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'canonical';
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }
    setMeta('og:title', ogTitle || title, 'property');
    setMeta('og:description', ogDescription || description, 'property');
    setMeta('og:type', ogType, 'property');
    if (canonicalUrl) setMeta('og:url', canonicalUrl, 'property');
    setMeta('twitter:title', ogTitle || title);
    setMeta('twitter:description', ogDescription || description);

    const scriptIds: string[] = [];
    if (schemaJsonLd && schemaJsonLd.length > 0) {
      schemaJsonLd.forEach((schema, idx) => {
        const scriptId = `jsonld-seo-${idx}`;
        scriptIds.push(scriptId);
        let script = document.getElementById(scriptId) as HTMLScriptElement;
        if (!script) {
          script = document.createElement('script');
          script.type = 'application/ld+json';
          script.id = scriptId;
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(schema);
      });
    }

    return () => {
      scriptIds.forEach((sid) => {
        const s = document.getElementById(sid);
        if (s) s.remove();
      });
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogType, schemaJsonLd]);

  return null;
}